import type { ProductT } from '@/features/Product/types/types';
import type { ScrapingJob } from '@/features/Scraping/api/dto';

import type { ScrapingJobSelection } from '../../model/types';

export type JobSelectionPayload = {
  jobs: ScrapingJobSelection[];
  productFilter: FilterKeyValue[];
  dateRange: { startDate: string; endDate: string };
  unmatchedCount: number;
};

/**
 * 선택된 scraping job 목록으로부터 product filter, dateRange, job 목록을 생성하는 순수 함수
 *
 * - product_name → product_id 매핑
 * - date range 집계 (min start, max end)
 * - ScrapingJobSelection[] 변환
 */
export const buildJobSelectionPayload = (
  selectedJobs: ScrapingJob[],
  productMap: Map<string, ProductT>,
): JobSelectionPayload => {
  // Product 매핑: product_name → product_id
  const matchedProducts: FilterKeyValue[] = [];
  let unmatchedCount = 0;

  for (const job of selectedJobs) {
    const productName = job.scraper_input?.product_name;
    if (!productName) continue;

    // productMap values에서 이름으로 검색
    let matched = false;
    for (const [productId, product] of productMap.entries()) {
      if (product.product_name === productName) {
        // 중복 방지
        if (!matchedProducts.some((p) => p.filterKey === productId)) {
          matchedProducts.push({
            filterKey: productId,
            filterName: productName,
          });
        }
        matched = true;
        break;
      }
    }

    if (!matched) {
      unmatchedCount++;
    }
  }

  // Date range: min(start_date), max(end_date)
  const startDates = selectedJobs
    .map((j) => j.start_date)
    .filter((d): d is string => !!d);
  const endDates = selectedJobs
    .map((j) => j.end_date)
    .filter((d): d is string => !!d);

  const dateRange = {
    startDate: startDates.length > 0 ? startDates.sort()[0] : '',
    endDate: endDates.length > 0 ? endDates.sort().reverse()[0] : '',
  };

  // ScrapingJobSelection 목록
  const jobs: ScrapingJobSelection[] = selectedJobs.map((job) => ({
    alias: job.alias,
    title: job.title,
    productName: job.scraper_input?.product_name,
    startDate: job.start_date,
    endDate: job.end_date,
  }));

  return { jobs, productFilter: matchedProducts, dateRange, unmatchedCount };
};
